import type ideographicDescriptionCharacters from "@-/com.npmjs.ideographic-description-characters";
import { staticTable } from "@-/dev.improve.haniverse.database.schemas.static._lib.table";
import { bool, index, integer, text } from "@-/dev.improve.haniverse.lib.database.table";

type IdsCharacter =
  (typeof ideographicDescriptionCharacters)[keyof typeof ideographicDescriptionCharacters];

export const graphemeTable = staticTable(
  "grapheme",
  {
    glyph: text("glyph").notNull(),

    /** How many distinct compound graphemes use this grapheme as a component. */
    compoundsCount: integer("compounds_count").notNull().default(0),

    /** The SOURCE face that supplies this glyph's outline in Han Composite; null means the Noto Sans SC/TC default. Build-time provenance — the app renders everything with Han Composite. */
    fontFamily: text("font_family"),

    /** IDS decomposition: operator followed by grapheme.id refs for each component. */
    ids: text("ids", { mode: "json" }).$type<[IdsCharacter, ...number[]]>(),

    strokeCount: integer("stroke_count"),

    // Stroke paths/medians live in the 1:1 `grapheme_stroke_data` child table, not inline —
    // the encoded blob dwarfs the rest of the row and bloated every `grapheme` page. See
    // `grapheme-stroke-data-table.ts`.

    /**
     * Whether this grapheme is applicable within Mainland China (PRC).
     * Influences whether or not it shows up in search.
     */
    prcApplicable: bool("prc_applicable").notNull(),

    /**
     * Whether this grapheme is applicable within Taiwan.
     * Influences whether or not it shows up in search.
     **/
    twApplicable: bool("tw_applicable").notNull(),

    /**
     * Whether this grapheme is applicable within Hong Kong.
     * Influences whether or not it shows up in search.
     **/
    hkApplicable: bool("hk_applicable").notNull(),
  },
  (table) => [index("grapheme_glyph_idx").on(table.glyph)],
);

export type Grapheme = typeof graphemeTable.$inferSelect;
